import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onPurgeProcessed: (olderThanDays: number) => Promise<void>;
    isPurging?: boolean;
};

const IntegrationMessagesPurgeDialog = ({
    open,
    onOpenChange,
    onPurgeProcessed,
    isPurging,
}: Props) => {
    const { t } = useTranslation();
    const [olderThanDays, setOlderThanDays] = useState('0');
    const [confirmText, setConfirmText] = useState('');

    const handlePurge = async () => {
        const days = Number(olderThanDays);
        if (!Number.isFinite(days) || days < 0) return;
        await onPurgeProcessed(days);
        setConfirmText('');
        onOpenChange(false);
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            setConfirmText('');
        }
        onOpenChange(nextOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('integrationMessages.purge.title')}</DialogTitle>
                    <DialogDescription>{t('integrationMessages.purge.description')}</DialogDescription>
                </DialogHeader>

                <label className="flex flex-col gap-1 text-sm">
                    <span>{t('integrationMessages.purge.daysLabel')}</span>
                    <input
                        type="number"
                        min={0}
                        value={olderThanDays}
                        onChange={(event) => setOlderThanDays(event.target.value)}
                        className="rounded-lg border border-lightgray bg-base px-3 py-2"
                    />
                    <span className="text-muted-foreground text-xs">
                        {t('integrationMessages.purge.daysHint')}
                    </span>
                </label>

                <label className="flex flex-col gap-1 text-sm">
                    <span>{t('integrationMessages.purge.confirmLabel')}</span>
                    <input
                        type="text"
                        value={confirmText}
                        onChange={(event) => setConfirmText(event.target.value)}
                        className="rounded-lg border border-lightgray bg-base px-3 py-2"
                        placeholder={t('integrationMessages.purge.confirmPlaceholder')}
                    />
                </label>

                <DialogFooter>
                    <Button variant="outline" onClick={() => handleOpenChange(false)}>
                        {t('integrationMessages.actions.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        disabled={
                            confirmText !== t('integrationMessages.purge.confirmWord') || isPurging
                        }
                        onClick={() => void handlePurge()}
                    >
                        {t('integrationMessages.actions.purgeConfirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default IntegrationMessagesPurgeDialog;
